// Corbis Soft AI assistant for FBR Smart's React app.
//
// 1. Copy this file to src/layout/AiAssistant.tsx.
// 2. In .env (Vite):  VITE_AI_ASSISTANT_URL=https://ai.example.com/fbr/
// 3. Mounted in src/layout/AppShell.tsx (wraps every signed-in page in every app — Layout.tsx is
//    not used by Accounting, Inventory, Settings, Auto Workshop, Auto Dealers or Fueling Station).
//
// The assistant runs on the AI server (an iframe from widget.js). This component only loads the
// widget and hands it the signed-in user's access token when it asks, refreshing the token first
// when the AI server refused it. Nothing else in FBR Smart changes.
import { useEffect } from 'react';
import { apiFetch } from '../api/httpClient';
import { getAccessToken } from '../api/tokenStorage';

type TokenReply = string | { token: string; expiresIn?: number };

type AIRagWidget = {
    version: string;
    init(options: {
        getToken: (request: { refresh: boolean }) => Promise<TokenReply>;
        title?: string;
        position?: 'left' | 'right';
    }): void;
    open(): void;
    close(): void;
    destroy(): void;
};

declare global {
    interface Window {
        AIRagWidget?: AIRagWidget;
    }
}

const SCRIPT_ID = 'airag-widget-script';

function loadWidget(baseUrl: string): Promise<AIRagWidget> {
    if (window.AIRagWidget) return Promise.resolve(window.AIRagWidget);
    return new Promise((resolve, reject) => {
        let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        if (!script) {
            script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.src = new URL('widget.js', baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`).href;
            script.async = true;
            document.body.appendChild(script);
        }
        script.addEventListener('load', () =>
            window.AIRagWidget ? resolve(window.AIRagWidget) : reject(new Error('AI widget not found')),
        );
        script.addEventListener('error', () => reject(new Error('AI widget could not be loaded')));
    });
}

async function currentToken({ refresh }: { refresh: boolean }): Promise<TokenReply> {
    if (refresh) {
        // Any authenticated call renews an expired access token (apiFetch refreshes on 401).
        await apiFetch('/api/auth/me').catch(() => undefined);
    }
    return getAccessToken() ?? '';
}

type Props = {
    /** The AI server's FBR Smart address, e.g. https://ai.example.com/fbr/ */
    baseUrl?: string;
    title?: string;
    position?: 'left' | 'right';
};

export default function AiAssistant({
    baseUrl = import.meta.env.VITE_AI_ASSISTANT_URL as string | undefined,
    title = 'Corbis Soft AI',
    position = 'right',
}: Props) {
    useEffect(() => {
        if (!baseUrl || !getAccessToken()) return;
        let active = true;
        loadWidget(baseUrl)
            .then((widget) => {
                if (active) widget.init({ getToken: currentToken, title, position });
            })
            .catch((error: unknown) => console.warn('Corbis Soft AI:', error));
        return () => {
            active = false;
            window.AIRagWidget?.destroy();
        };
    }, [baseUrl, title, position]);

    return null;
}
